// The live season's settings, as the client sees them (PLAN-01 Stream D3).
//
// The chain is deliberately: network, then the last validated season cached
// locally, then the bundled defaults in season-defaults.json. A season is
// something the player plays a whole week of, so a train tunnel must not
// re-price the game or hand the player Normal-mode numbers in Veteran.
//
// The hash check is the point of this file. A Veteran season is scored by
// replaying the client's action log server-side against these numbers, so a
// config that was altered after the season was locked would make the server
// score a season the player never played. The client re-derives the digest from
// the config text it actually received and refuses the season if it does not
// match what the database recorded at lock time.

import items from '../data/season-defaults.json';
import { validateSeasonConfig, type SeasonConfig } from './seasonConfig.schema';
import { readConfig, loadSession, type SupabaseConfig } from './supabase';
import { BREEDS } from '../sim/config';
import { BUNDLED_SEASON_ID } from '../sim/season';

/** PostgREST schema holding every Clay Farm table. Never bare `public`. */
const SCHEMA = 'clayfarm';

export type SeasonFormat = 'sprint' | 'raise';

export type SeasonSource = 'network' | 'cache' | 'defaults';

export interface LiveSeason {
  id: string;
  format: SeasonFormat;
  leagueCity: string;
  latitude: number;
  longitude: number;
  startsAt: string;
  endsAt: string;
  config: SeasonConfig;
  /** sha256 hex of the canonical config text, as recorded at lock time. */
  configHash: string;
  source: SeasonSource;
}

export type SeasonError =
  | 'not_configured'
  | 'no_season'
  | 'unauthorized'
  | 'server'
  | 'offline'
  | 'config_invalid'
  | 'hash_mismatch'
  | 'config_inconsistent'
  | 'verifier_unavailable';

export type SeasonResult =
  | { ok: true; season: LiveSeason }
  | { ok: false; error: SeasonError; detail?: string };

/**
 * Serialises a value exactly the way Postgres renders `jsonb::text`.
 *
 * This exists only as a FALLBACK. The server stores the canonical text in
 * `seasons.config_text` and hashes those bytes, so `configTextFor` prefers it and
 * the browser never has to agree with Postgres about formatting at all.
 *
 * It still has to be right, because a row locked before `config_text` existed has
 * no stored text, and falling through to a wrong-but-plausible serialisation
 * would report a perfectly good season as tampered. Both details below were
 * verified against a live row on 2026-09-26 (PLAN-01 Stream D5):
 *
 *  - Key order is shortest-first, then bytewise — `keycmp` in jsonb.c — which is
 *    NOT the plain lexicographic order most people assume, and not insertion
 *    order like JSON.stringify.
 *  - The separators are `": "` and `", "`, with no padding inside the braces or
 *    brackets. jsonb::text is pretty-printed; JSON.stringify is compact. Getting
 *    only the key order right still produces a different hash, because
 *    `{"a":1}` and `{"a": 1}` are different strings.
 *
 * The fixture in seasonConfig.jsonb.test.ts is a real config_text captured from
 * the live database, so a formatting change cannot pass unnoticed.
 */
export function jsonbText(value: unknown): string {
  if (value === null) return 'null';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') return JSON.stringify(value);
  if (typeof value === 'string') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(jsonbText).join(', ')}]`;
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>).sort(
      ([a], [b]) => a.length - b.length || (a < b ? -1 : a > b ? 1 : 0),
    );
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}: ${jsonbText(v)}`).join(', ')}}`;
  }
  throw new Error(`[season] cannot serialise ${typeof value} as jsonb`);
}

/** sha256 hex, same digest the `seasons_guard` trigger computes server-side. */
export async function sha256Hex(text: string): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) throw new Error('[season] no WebCrypto in this environment');
  const digest = await subtle.digest('SHA-256', new TextEncoder().encode(text));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

// --- local cache ------------------------------------------------------------

export interface SeasonCache {
  read(): Promise<LiveSeason | null>;
  write(season: LiveSeason): Promise<void>;
}

const CACHE_DB = 'clayfarm-season';
const CACHE_STORE = 'seasons';
const CACHE_KEY = 'live';
const CACHE_LS_KEY = 'clayfarm.season.live';

function readCacheRow(raw: string | null | undefined): LiveSeason | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as LiveSeason;
    // A cache hit still has to be a config that passes today's validator: the
    // shape can change between releases and a stale row must not skip it.
    if (!parsed || typeof parsed.id !== 'string' || typeof parsed.configHash !== 'string') {
      return null;
    }
    const result = validateSeasonConfig(upgradeLockedConfig(parsed.config));
    if (!result.ok) return null;
    return { ...parsed, config: result.config, source: 'cache' };
  } catch {
    return null;
  }
}

function openCacheDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(CACHE_DB, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(CACHE_STORE)) {
        request.result.createObjectStore(CACHE_STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function cacheTx<T>(
  db: IDBDatabase,
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const request = run(db.transaction(CACHE_STORE, mode).objectStore(CACHE_STORE));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * IndexedDB first, localStorage second, silent no-op last — the same ladder
 * persistence.ts uses for saves, because a player with storage disabled should
 * still get the network season and the bundled defaults.
 */
export const defaultSeasonCache: SeasonCache = {
  async read() {
    try {
      const db = await openCacheDb();
      return readCacheRow(await cacheTx<string | undefined>(db, 'readonly', (s) => s.get(CACHE_KEY)));
    } catch {
      try {
        return readCacheRow(localStorage.getItem(CACHE_LS_KEY));
      } catch {
        return null;
      }
    }
  },
  async write(season) {
    try {
      const db = await openCacheDb();
      await cacheTx(db, 'readwrite', (s) => s.put(JSON.stringify(season), CACHE_KEY));
    } catch {
      try {
        localStorage.setItem(CACHE_LS_KEY, JSON.stringify(season));
      } catch {
        // Nothing to do: the cache is an optimisation, never a requirement.
      }
    }
  },
};

// --- the read path ----------------------------------------------------------

/** Item ids from the catalog, used to reject a config with an unpriced typo. */
const ITEM_IDS = new Set<string>(Object.keys(items.config.prices));

/** Breeds the sim can spawn, used to reject a chick price the game never charges. */
const BREED_IDS = new Set<string>(Object.keys(BREEDS));

/**
 * The fields a season locked before the buy and feed screens took their numbers
 * off the config (PLAN-01 Stream D6). A row locked earlier carries none of them,
 * and the validator now insists on all of them, so the read path fills just the
 * missing ones from the bundle and lets the validator judge the result.
 *
 * Cost, stated plainly: a season locked before this change plays at the bundle's
 * chick, sexed and bag numbers — the ones it was locked with — until the owner
 * re-locks it, and a server replay of that season would have to score those same
 * numbers. The alternative is refusing a season the owner has already committed
 * to, which strands the player on the bundle for a whole week.
 */
const LATE_SEASON_FIELDS = [
  'chickPrices',
  'chickBudget',
  'sexedChickMultiplier',
  'sexedHenRate',
  'bagSizes',
] as const;

function upgradeLockedConfig(value: unknown): unknown {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return value;
  const row = value as Record<string, unknown>;
  const missing = LATE_SEASON_FIELDS.filter((field) => row[field] === undefined);
  if (missing.length === 0) return row;
  const bundle = items.config as unknown as Record<string, unknown>;
  const upgraded: Record<string, unknown> = { ...row };
  for (const field of missing) upgraded[field] = bundle[field];
  return upgraded;
}

const DEFAULTS = ((): LiveSeason => {
  // The bundle is never upgraded: if it lost one of the late fields it should
  // throw here rather than quietly paper over itself.
  const result = validateSeasonConfig(items.config, {
    itemIds: ITEM_IDS,
    breedIds: BREED_IDS,
  });
  if (!result.ok) {
    throw new Error(`[season] bundled defaults are invalid: ${result.errors.join('; ')}`);
  }
  return {
    id: BUNDLED_SEASON_ID,
    format: 'sprint',
    leagueCity: 'San Juan, PR',
    latitude: 18.4655,
    longitude: -66.1057,
    startsAt: '',
    endsAt: '',
    config: result.config,
    // The defaults are never server-locked, so there is no hash to match. An
    // empty hash is the honest value: nothing has attested to these numbers.
    configHash: '',
    source: 'defaults',
  };
})();

export function bundledDefaultSeason(): LiveSeason {
  return DEFAULTS;
}

interface SeasonRow {
  id?: unknown;
  format?: unknown;
  league_city?: unknown;
  latitude?: unknown;
  longitude?: unknown;
  starts_at?: unknown;
  ends_at?: unknown;
  config?: unknown;
  config_hash?: unknown;
}

/**
 * The exact text the hash covers.
 *
 * `config_text` is authoritative and is what the deployed `seasons_guard` stores
 * and hashes on lock. It is preferred whenever present so the client never has to
 * reimplement Postgres's jsonb formatting. `jsonbText` is the fallback for rows
 * locked before the column existed, and it is byte-accurate against a live row
 * (see the tests), so the fallback is a repair rather than a guess.
 */
function configTextFor(row: SeasonRow): string {
  const explicit = (row as { config_text?: unknown }).config_text;
  if (typeof explicit === 'string' && explicit.length > 0) return explicit;
  return jsonbText(row.config);
}

async function verify(row: SeasonRow): Promise<LiveSeasonResult> {
  // Upgraded, not edited: the hash below covers the row's own bytes, and the
  // season the game plays is the upgraded config re-validated.
  const shape = validateSeasonConfig(upgradeLockedConfig(row.config), {
    itemIds: ITEM_IDS,
    breedIds: BREED_IDS,
  });
  if (!shape.ok) {
    return { ok: false, error: 'config_invalid', detail: shape.errors.join('; ') };
  }
  if (typeof row.config_hash !== 'string' || row.config_hash.length === 0) {
    return { ok: false, error: 'hash_mismatch', detail: 'season has no config_hash' };
  }

  // The two halves of the row must describe the same season. Without this, a row
  // whose `config` says one thing and whose `config_text` (and therefore hash)
  // says another would pass: the hash is checked over the text while the game
  // plays the parsed object. `config_text` is only ever a cached rendering of
  // `config`, so any difference means the row is inconsistent or was tampered
  // with — and the game is about to cost a player real money at these numbers.
  const text = configTextFor(row);
  let rebuilt: string;
  try {
    rebuilt = jsonbText(row.config);
  } catch (err) {
    return { ok: false, error: 'verifier_unavailable', detail: String(err) };
  }
  if (rebuilt !== text) {
    return {
      ok: false,
      error: 'config_inconsistent',
      detail: 'config_text does not match config; the row describes two different seasons',
    };
  }

  let computed: string;
  try {
    computed = await sha256Hex(text);
  } catch (err) {
    return { ok: false, error: 'verifier_unavailable', detail: String(err) };
  }
  if (computed !== row.config_hash.toLowerCase()) {
    return {
      ok: false,
      error: 'hash_mismatch',
      detail: `expected ${row.config_hash}, config text hashes to ${computed}`,
    };
  }
  if (
    typeof row.id !== 'string' ||
    (row.format !== 'sprint' && row.format !== 'raise') ||
    typeof row.league_city !== 'string' ||
    typeof row.latitude !== 'number' ||
    typeof row.longitude !== 'number' ||
    typeof row.starts_at !== 'string' ||
    typeof row.ends_at !== 'string'
  ) {
    return { ok: false, error: 'config_invalid', detail: 'season row is missing required columns' };
  }
  return {
    ok: true,
    season: {
      id: row.id,
      format: row.format,
      leagueCity: row.league_city,
      latitude: row.latitude,
      longitude: row.longitude,
      startsAt: row.starts_at,
      endsAt: row.ends_at,
      config: shape.config,
      configHash: computed,
      source: 'network',
    },
  };
}

type LiveSeasonResult = SeasonResult;

export interface FetchSeasonDeps {
  config?: SupabaseConfig | null;
  fetchImpl?: typeof fetch;
  cache?: SeasonCache;
  /** Injected so the test can assert the "no season live" branch without a DB. */
  now?: () => string;
}

async function requestLiveRow(
  config: SupabaseConfig,
  fetchImpl: typeof fetch,
): Promise<{ rows: SeasonRow[] } | { error: SeasonError; detail?: string }> {
  const session = loadSession();
  try {
    const response = await fetchImpl(
      `${config.url}/rest/v1/seasons?status=eq.live&select=*&order=starts_at.desc&limit=1`,
      {
        headers: {
          apikey: config.anonKey,
          authorization: `Bearer ${session?.accessToken ?? config.anonKey}`,
          'content-type': 'application/json',
          'accept-profile': SCHEMA,
        },
      },
    );
    if (response.status === 401 || response.status === 403) return { error: 'unauthorized' };
    if (!response.ok) return { error: 'server', detail: `${response.status}` };
    const rows = (await response.json()) as SeasonRow[];
    return { rows: Array.isArray(rows) ? rows : [] };
  } catch (err) {
    return { error: 'offline', detail: String(err) };
  }
}

/**
 * The live season, from wherever it can be honestly obtained.
 *
 * A `hash_mismatch` or `config_invalid` is NOT a reason to fall through to the
 * cache: that would quietly swap in different prices for a season the server
 * has already committed to scoring. Only a failed fetch falls back.
 */
export async function fetchLiveSeason(deps: FetchSeasonDeps = {}): Promise<SeasonResult> {
  const config = deps.config === undefined ? readConfig() : deps.config;
  const fetchImpl = deps.fetchImpl ?? globalThis.fetch?.bind(globalThis);
  const cache = deps.cache ?? defaultSeasonCache;

  if (!config) return { ok: false, error: 'not_configured' };
  if (!fetchImpl) return { ok: false, error: 'offline' };

  const response = await requestLiveRow(config, fetchImpl);
  if ('error' in response) {
    const cached = await cache.read();
    // `source` describes where THIS result came from, so it is stamped on the
    // way out rather than trusted from whatever shape the cache stored.
    if (cached) return { ok: true, season: { ...cached, source: 'cache' } };
    // A dead network is the only failure that falls all the way through to the
    // bundle. A 401 or a 500 is a misconfiguration, and silently re-pricing the
    // game with REFOCUS defaults would hide it until someone read a log.
    if (response.error === 'offline') return { ok: true, season: DEFAULTS };
    return { ok: false, error: response.error, detail: response.detail };
  }

  const row = response.rows[0];
  if (!row) {
    // A live season that has not started yet is a normal state between seasons,
    // so the cache and then the defaults are the right answer, not an error.
    const cached = await cache.read();
    if (cached) return { ok: true, season: { ...cached, source: 'cache' } };
    return { ok: true, season: DEFAULTS };
  }

  const verified = await verify(row);
  if (!verified.ok) return verified;

  await cache.write(verified.season);
  return verified;
}

/**
 * Stamps the save with the season it was played under, so the future server
 * replay (REFOCUS §11 anti-cheat) knows which config produced the action log.
 * `seasonId` and `configHash` together, never one without the other: a season
 * id alone cannot tell the server which numbers were in force.
 */
export interface SeasonSaveStamp {
  seasonId: string;
  configHash: string;
  /** True when the numbers came from the bundle rather than a locked season. */
  unverified?: boolean;
}

export function recordSeasonOnSave(
  state: { season?: SeasonSaveStamp },
  season: LiveSeason,
): void {
  state.season = {
    seasonId: season.id,
    configHash: season.configHash,
    ...(season.configHash ? {} : { unverified: true }),
  };
}
